import type { RunItemView } from '../models/insightAction.types'
import type {
  MaybeUnknown,
  ResolutionReason,
  RevertBlockedReason,
  RuntimeOutcomeReason,
} from '../models/insightActionReason'
import { chipTailKey, errorKey, reasonKeys, revertBlockedKey } from '../service/reasonText'

import { useI18n } from '@/shared/i18n'

export interface ReasonSentence {
  label: string
  description: string
  rawCode: string
}

export interface UseReasonText {
  reason: (
    code?: MaybeUnknown<ResolutionReason | RuntimeOutcomeReason> | null,
    channel?: string | null,
  ) => ReasonSentence
  reasonLabel: (
    code?: MaybeUnknown<ResolutionReason | RuntimeOutcomeReason> | null,
    channel?: string | null,
  ) => string
  chipTail: (code?: MaybeUnknown<ResolutionReason> | null) => string
  revertBlocked: (code?: RevertBlockedReason | null) => string
  requestError: (code?: string | null) => { message: string; code: string }
  itemReason: (item: Pick<RunItemView, 'errorMessage' | 'reasonCode'>) => string
}

const EMPTY: ReasonSentence = { label: '', description: '', rawCode: '' }

export function useReasonText(): UseReasonText {
  const { t } = useI18n()

  function reason(
    code?: MaybeUnknown<ResolutionReason | RuntimeOutcomeReason> | null,
    channel?: string | null,
  ): ReasonSentence {
    const keys = reasonKeys(code)
    if (!keys) return EMPTY
    const named = { channel: channel || t('insightActions.reason.thisChannel') }
    return {
      label: t(keys.label, named),
      description: t(keys.description, named),
      rawCode: keys.unknown ? String(code) : '',
    }
  }

  return {
    reason,
    reasonLabel: (code, channel) => reason(code, channel).label,
    chipTail: (code) => t(chipTailKey(code)),
    revertBlocked: (code) => t(revertBlockedKey(code)),
    requestError: (code) => ({ message: t(errorKey(code)), code: code ?? '' }),
    itemReason: (item) => item.errorMessage || reason(item.reasonCode).description,
  }
}
